/**
 * Issue-tracker logos, looked up by the `logoKey` in a tracker's description
 *.
 *
 * The one place on the frontend that knows what any tracker looks like.
 * Every screen that shows a tracker draws it through here, so a tracker with
 * no entry still appears, as a plain tile with its first letter, and adding
 * one is an edit to this file alone. Names and words come from the
 * description (`useTrackerDescriptors`), never from here.
 */
import type { ReactElement } from 'react'

/** The Jira logo mark — the three cascading Atlassian-blue chevrons. Rendered
 * as an inline SVG (the same hand-authored approach as Slack's `SlackLogoBlock`
 * on the Integrations page) so it needs no external asset. `mono` fills every
 * path with `currentColor` for on-a-coloured-button use, where the gradient
 * would have no contrast. */
function JiraLogo({ className, mono = false }: { className?: string; mono?: boolean }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      {!mono && (
        <defs>
          <linearGradient id="jira-mark-grad" x1="1" y1="0" x2="0.3" y2="1">
            <stop offset="0" stopColor="#2684FF" />
            <stop offset="1" stopColor="#0052CC" />
          </linearGradient>
        </defs>
      )}
      <path fill={mono ? 'currentColor' : '#0052CC'} d="M30.4 0H15.9c0 3.6 2.9 6.6 6.6 6.6h2.7v2.6c0 3.6 2.9 6.6 6.6 6.6V1.1C31.8.5 31.2 0 30.4 0Z" />
      <path fill={mono ? 'currentColor' : 'url(#jira-mark-grad)'} d="M23.2 7.3H8.7c0 3.6 2.9 6.6 6.6 6.6H18v2.6c0 3.6 3 6.6 6.6 6.6V8.3c0-.6-.6-1-1.4-1Z" />
      <path fill={mono ? 'currentColor' : 'url(#jira-mark-grad)'} d="M15.9 14.5H1.4c0 3.7 3 6.6 6.6 6.6h2.7v2.6c0 3.6 3 6.6 6.6 6.6V15.6c0-.6-.6-1.1-1.4-1.1Z" />
    </svg>
  )
}

/** The GitHub mark (the octocat), the same outline the GitHub card on the
 * Integrations page draws. Drawn on its dark tile, since the mark itself is
 * one colour; `mono` draws the bare mark in the current text colour, for a
 * coloured button. */
function GitHubLogo({ className, mono = false }: { className?: string; mono?: boolean }) {
  const mark = (
    <svg viewBox="0 0 24 24" className={mono ? className : 'h-[60%] w-[60%] fill-white'} fill={mono ? 'currentColor' : undefined} aria-hidden="true">
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.55 0-.27-.01-1.17-.02-2.12-3.2.69-3.87-1.36-3.87-1.36-.52-1.33-1.28-1.68-1.28-1.68-1.05-.71.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.69 1.25 3.35.95.1-.75.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.71 0-1.26.45-2.29 1.19-3.1-.12-.29-.51-1.47.11-3.06 0 0 .97-.31 3.18 1.18.92-.26 1.91-.39 2.89-.39.98 0 1.97.13 2.89.39 2.21-1.49 3.18-1.18 3.18-1.18.62 1.59.23 2.77.11 3.06.74.81 1.19 1.84 1.19 3.1 0 4.44-2.7 5.41-5.27 5.7.41.36.78 1.06.78 2.14 0 1.55-.01 2.79-.01 3.17 0 .3.21.67.8.55C20.21 21.39 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5z" />
    </svg>
  )
  if (mono) return mark
  return (
    <div className={`flex items-center justify-center rounded-lg bg-[#24292f] ${className ?? ''}`} aria-hidden="true">
      {mark}
    </div>
  )
}

interface Brand {
  /** The full logo, when the tracker has one. */
  Logo?: (props: { className?: string; mono?: boolean }) => ReactElement
  /** Background for the small letter tile. */
  tileClass: string
  /** Background for a "Connect" button in the tracker's colour. */
  buttonClass: string
}

const BRANDS: Record<string, Brand> = {
  jira: { Logo: JiraLogo, tileClass: 'bg-[#0052cc]', buttonClass: 'bg-[#0052cc] hover:bg-[#0747a6]' },
  github: { Logo: GitHubLogo, tileClass: 'bg-[#24292f]', buttonClass: 'bg-[#24292f] hover:bg-[#32383f]' },
}

const PLAIN: Brand = { tileClass: 'bg-muted-foreground/70', buttonClass: 'bg-primary hover:bg-primary/90' }

function brandFor(logoKey: string | undefined): Brand {
  return (logoKey && BRANDS[logoKey]) || PLAIN
}

function initial(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase()
}

/** The small square tile: the tracker's first letter on its colour. Used in
 * card headers and on the suggested-test badge. */
export function TrackerTile({ logoKey, name, className }: { logoKey: string | undefined; name: string; className?: string }) {
  return (
    <div
      className={`flex items-center justify-center rounded-md text-white font-extrabold ${brandFor(logoKey).tileClass} ${className ?? ''}`}
      aria-hidden="true"
    >
      {initial(name)}
    </div>
  )
}

/** The tile's background class alone, for screens that draw their own tile
 * shape. */
export function trackerTileClass(logoKey: string | undefined): string {
  return brandFor(logoKey).tileClass
}

/** The tracker's full logo, or its tile when it has none. `mono` draws the
 * logo in the current text colour, for use on a coloured button. */
export function TrackerLogo({
  logoKey,
  name,
  className,
  mono = false,
}: {
  logoKey: string | undefined
  name: string
  className?: string
  mono?: boolean
}) {
  const { Logo } = brandFor(logoKey)
  if (Logo) return <Logo className={className} mono={mono} />
  return <TrackerTile logoKey={logoKey} name={name} className={`rounded-lg text-sm ${className ?? ''}`} />
}

/** Background classes for a "Connect <tracker>" button. */
export function trackerButtonClass(logoKey: string | undefined): string {
  return brandFor(logoKey).buttonClass
}
